import {inject, Injectable, signal} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {environment} from "../environments/environment";
import {
  ApartmentDto,
  LotteryDetailDto,
  LotteryPhotoDto,
  LotteryPhotoUploadTarget,
  UploadLotteryPhotoCommand,
  ReorderLotteryPhotosCommand,
  DeleteLotteryPhotoCommand,
  SetMainLotteryPhotoCommand,
} from "admin-api-dto";
import {BehaviorSubject, map} from "rxjs";

@Injectable({
  providedIn: 'root'
})
export class ApartmentService {

  constructor() {
    this.retrieveApartments();
  }

  private readonly http = inject(HttpClient);

  public readonly apartments$ = new BehaviorSubject<ApartmentDto[]>([]);

  private retrieveApartments() {
    this.http.get<ApartmentDto[]>(`${environment.apiUrl}/api/private/apartments`).subscribe(
      value => this.apartments$.next(value)
    )
  }

  public requestLotteryEligibilityExport() {
    return this.http.get<{ s3Key: string }>(`${environment.apiUrl}/api/private/apartments/eligibility-csv`);
  }

  public getLotteryEligibilityDownloadUrl(s3Key: string) {
    return this.http.get<{ url: string }>(`${environment.apiUrl}/api/private/apartments/eligibility-csv/signed`, {
      params: {s3Key}
    });
  }

  public requestApplicationsExport() {
    return this.http.get<{ s3Key: string }>(`${environment.apiUrl}/api/private/apartments/applications-csv`);
  }

  public getApplicationsDownloadUrl(s3Key: string) {
    return this.http.get<{ url: string }>(`${environment.apiUrl}/api/private/apartments/applications-csv/signed`, {
      params: {s3Key}
    });
  }

  public getApartment(id: string) {
    return this.apartments$.pipe(
      map(
        apartments => apartments.find(apartment => apartment.id === id)!
      )
    )
  }

  public getLotteryPhotos(id: string) {
    return this.http.get<LotteryDetailDto>(`${environment.apiUrl}/api/private/apartments/${id}/photos`);
  }

  public uploadLotteryPhoto(id: string, command: UploadLotteryPhotoCommand) {
    return this.http.post<LotteryPhotoDto>(`${environment.apiUrl}/api/private/apartments/${id}/photos`, command);
  }

  public getLotteryPhotoUploadTarget(id: string, fileName: string, contentType?: string) {
    return this.http.post<LotteryPhotoUploadTarget>(
      `${environment.apiUrl}/api/private/apartments/${id}/photos/upload-target`,
      {
        fileName,
        contentType,
      }
    );
  }

  public reorderLotteryPhotos(id: string, command: ReorderLotteryPhotosCommand) {
    return this.http.put<LotteryDetailDto>(`${environment.apiUrl}/api/private/apartments/${id}/photos/order`, command);
  }

  public setMainLotteryPhoto(id: string, command: SetMainLotteryPhotoCommand) {
    return this.http.put<LotteryPhotoDto>(`${environment.apiUrl}/api/private/apartments/${id}/photos/main`, command);
  }

  public deleteLotteryPhoto(id: string, command: DeleteLotteryPhotoCommand) {
    return this.http.request<void>('delete', `${environment.apiUrl}/api/private/apartments/${id}/photos`, {body: command});
  }
}
